/**
 * Retomada de contato com quem não fechou.
 *
 * O desenho é o da tela de atrasos do Linha Direta, porque ele funciona: uma
 * família por vez, de cima para baixo, texto pronto, humano decide. Clicar
 * marca como contatada e a linha some — o próximo fica no topo.
 *
 * O que ela **não** faz: disparar em massa. Retomada é conversa, e conversa em
 * lote a família reconhece de longe.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { get, post } from "../lib/api";
import { useAuth } from "../lib/auth";
import { dataHoraCurta } from "../lib/formato";
import { telefoneExibicao } from "../lib/paises";
import { linkWhatsapp, mensagemRetomada, MOTIVOS_SEM_RETOMADA } from "../lib/mensagensRetomada";
import { Cabecalho } from "../components/Layout";
import {
  COR_STATUS,
  MOTIVOS_NAO_MATRICULA,
  ROTULO_STATUS,
  type StatusVisita,
} from "../lib/tipos";

const VERDE = "#0e8c3a";

interface Item {
  id: string;
  nomeAluno: string;
  nomeResponsavel: string;
  telefoneResponsavel: string | null;
  paisTelefone?: string | null;
  inicioEm: string;
  status: StatusVisita;
  motivoNaoMatricula: string | null;
  curso: { nome: string } | null;
  serie: { nome: string } | null;
  contatadaEm: string | null;
  contatadaPor: string | null;
}

const STATUS_RETOMADA: StatusVisita[] = ["NAO_COMPARECEU", "CANCELADO", "OPTOU_OUTRA_ESCOLA"];

/** "há 3 meses" — a distância importa mais que a data exata. */
function haQuantoTempo(iso: string) {
  const dias = Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
  if (dias < 30) return `há ${dias} dia${dias === 1 ? "" : "s"}`;
  const meses = Math.round(dias / 30);
  return `há ${meses} ${meses === 1 ? "mês" : "meses"}`;
}

export default function Retomada() {
  const { podeAoMenos } = useAuth();
  const podeContatar = podeAoMenos("CONSULTOR");

  const [itens, setItens] = useState<Item[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState("");

  const [status, setStatus] = useState<StatusVisita[]>(STATUS_RETOMADA);
  const [meses, setMeses] = useState(12);
  const [busca, setBusca] = useState("");
  const [esconderContatadas, setEsconderContatadas] = useState(true);
  const [editando, setEditando] = useState<string | null>(null);
  const [textos, setTextos] = useState<Record<string, string>>({});

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const p = new URLSearchParams({ status: status.join(","), meses: String(meses) });
      if (busca) p.set("busca", busca);
      const r = await get<{ itens: Item[] }>(`/api/retomada?${p}`);
      setItens(r.itens);
      setErro("");
    } catch (e) {
      setErro(e instanceof Error ? e.message : "Falha ao carregar a lista");
    } finally {
      setCarregando(false);
    }
  }, [status, meses, busca]);

  useEffect(() => {
    const t = setTimeout(carregar, 300);
    return () => clearTimeout(t);
  }, [carregar]);

  const texto = (v: Item) => textos[v.id] ?? mensagemRetomada(v);

  async function abrirWhatsapp(v: Item) {
    const link = linkWhatsapp(v.telefoneResponsavel, texto(v), v.paisTelefone);
    if (!link) return setErro(`Telefone inválido para ${v.nomeResponsavel}`);
    window.open(link, "_blank", "noopener");
    try {
      const r = await post<{ contatadaEm: string }>(`/api/retomada/${v.id}/contato`);
      setItens((atual) =>
        atual.map((i) => (i.id === v.id ? { ...i, contatadaEm: r.contatadaEm, contatadaPor: "você" } : i)),
      );
    } catch {
      /* a conversa já abriu; falhar o registro não pode atrapalhar o envio */
    }
  }

  const visiveis = useMemo(
    () => itens.filter((v) => !(esconderContatadas && v.contatadaEm)),
    [itens, esconderContatadas],
  );
  const jaContatadas = itens.filter((v) => v.contatadaEm).length;

  const alternarStatus = (s: StatusVisita) =>
    setStatus((atual) => (atual.includes(s) ? atual.filter((x) => x !== s) : [...atual, s]));

  return (
    <>
      <Cabecalho titulo="Retomada de contato" />

      <div className="cartao" style={{ marginBottom: 16, display: "flex", gap: 14, flexWrap: "wrap", alignItems: "flex-end" }}>
        <div style={{ flex: "1 1 220px" }}>
          <label className="rotulo">Buscar</label>
          <input className="campo" placeholder="Aluno ou responsável" value={busca} onChange={(e) => setBusca(e.target.value)} />
        </div>
        <div style={{ flex: "0 1 160px" }}>
          <label className="rotulo">Período</label>
          <select className="campo" value={meses} onChange={(e) => setMeses(Number(e.target.value))}>
            <option value={6}>Últimos 6 meses</option>
            <option value={12}>Últimos 12 meses</option>
            <option value={24}>Últimos 2 anos</option>
            <option value={60}>Tudo</option>
          </select>
        </div>
        <div style={{ flex: "1 1 300px" }}>
          <label className="rotulo">Situação</label>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {STATUS_RETOMADA.map((s) => {
              const ativo = status.includes(s);
              return (
                <button key={s} type="button" onClick={() => alternarStatus(s)}
                  style={{ padding: "6px 12px", borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: "pointer",
                    border: `1px solid ${ativo ? COR_STATUS[s] : "#e4e7ec"}`,
                    background: ativo ? COR_STATUS[s] : "#fff", color: ativo ? "#fff" : "#667085" }}>
                  {ROTULO_STATUS[s]}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div className="cartao" style={{ marginBottom: 16, display: "flex", gap: 16, alignItems: "center", flexWrap: "wrap" }}>
        <span style={{ background: "#fef0c7", color: "#b54708", borderRadius: 20, padding: "4px 12px", fontSize: 13, fontWeight: 600 }}>
          para contatar: {itens.length - jaContatadas}
        </span>
        <span style={{ background: "#f0faf4", color: "#1a5c2e", borderRadius: 20, padding: "4px 12px", fontSize: 13, fontWeight: 600 }}>
          já contatadas: {jaContatadas} de {itens.length}
        </span>
        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: "#475467", cursor: "pointer" }}>
          <input type="checkbox" checked={esconderContatadas} onChange={(e) => setEsconderContatadas(e.target.checked)} />
          esconder quem já foi contatado
        </label>
      </div>

      <div style={{ background: "#eff8ff", border: "1px solid #b2d4f0", borderRadius: 10, padding: "14px 16px", marginBottom: 18, fontSize: 13, color: "#175cd3", lineHeight: 1.7 }}>
        <strong>Como funciona:</strong> vá de cima para baixo clicando em <strong>Abrir no WhatsApp</strong>.
        Cada clique abre a conversa da família já com o texto pronto — você confere, ajusta se quiser
        e aperta enviar lá. Ao voltar, a família sai da lista.
        <br />
        O texto muda conforme o motivo registrado. Clique em <strong>mensagem</strong> para editar antes de abrir.
        <br />
        É um envio por família, e tem de ser assim: retomada em lote a pessoa reconhece de longe.
      </div>

      {erro && <div className="erro" style={{ marginBottom: 12 }}>{erro}</div>}

      {carregando ? (
        <div style={{ color: "var(--texto-suave)", fontSize: 13 }}>Carregando...</div>
      ) : visiveis.length === 0 ? (
        <div className="cartao" style={{ textAlign: "center", color: "var(--texto-suave)", padding: 32, fontSize: 13 }}>
          {itens.length === 0
            ? "Nenhuma família nesse filtro."
            : "Todas as famílias do filtro já foram contatadas."}
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {visiveis.map((v) => {
            const delicado = MOTIVOS_SEM_RETOMADA.includes(v.motivoNaoMatricula ?? "");
            return (
              <div key={v.id} className="cartao" style={{ borderLeft: `4px solid ${COR_STATUS[v.status]}`, borderRadius: 10, opacity: v.contatadaEm ? 0.6 : 1 }}>
                <div style={{ display: "flex", gap: 14, alignItems: "center", flexWrap: "wrap" }}>
                  <div style={{ flex: 1, minWidth: 240 }}>
                    <div style={{ fontSize: 14, fontWeight: 600 }}>
                      {v.nomeAluno}
                      <span style={{ fontWeight: 400, color: "var(--texto-suave)", fontSize: 12 }}>
                        {" "}{v.serie?.nome ?? ""} {v.curso ? `· ${v.curso.nome}` : ""}
                      </span>
                      <span className="etiqueta" style={{ background: COR_STATUS[v.status], marginLeft: 8 }}>
                        {ROTULO_STATUS[v.status]}
                      </span>
                    </div>
                    <div style={{ fontSize: 12, color: "var(--texto-suave)", marginTop: 3 }}>
                      {v.nomeResponsavel} · {telefoneExibicao(v.telefoneResponsavel, v.paisTelefone)} · visitou {haQuantoTempo(v.inicioEm)}
                      {v.motivoNaoMatricula && ` · ${MOTIVOS_NAO_MATRICULA[v.motivoNaoMatricula] ?? v.motivoNaoMatricula}`}
                    </div>
                    {v.contatadaEm && (
                      <div style={{ fontSize: 11, color: VERDE, marginTop: 3 }}>
                        contatada {dataHoraCurta(v.contatadaEm)}
                        {v.contatadaPor ? ` por ${v.contatadaPor}` : ""}
                      </div>
                    )}
                    {delicado && (
                      <div style={{ fontSize: 11, color: "#b54708", marginTop: 4 }}>
                        Saiu por {MOTIVOS_NAO_MATRICULA[v.motivoNaoMatricula!]?.toLowerCase()} — insistir aqui
                        costuma incomodar mais do que ajudar.
                      </div>
                    )}
                  </div>

                  {podeContatar && (
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                      <button className="botao" onClick={() => abrirWhatsapp(v)} style={{ whiteSpace: "nowrap" }}>
                        ↗ Abrir no WhatsApp
                      </button>
                      <button className="botao secundario" onClick={() => setEditando(editando === v.id ? null : v.id)}>
                        mensagem
                      </button>
                    </div>
                  )}
                </div>

                {editando === v.id && (
                  <textarea
                    className="campo"
                    style={{ marginTop: 12, resize: "vertical", fontSize: 13 }}
                    rows={7}
                    value={texto(v)}
                    onChange={(e) => setTextos((t) => ({ ...t, [v.id]: e.target.value }))}
                  />
                )}
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}
